import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'

export type FilterParamSpec = {
  // Value the param is seeded with when absent from the URL. Written back once
  // on mount so the filter is deep-linkable from the first render (e.g. the
  // 'All' sentinel the anomalies and signals lists use).
  default?: string
  // Encode as a comma-separated list and expose as string[].
  multiple?: boolean
}

type ValueOf<S extends FilterParamSpec> = S extends {multiple: true} ? string[] : string

type Values<S extends Record<string, FilterParamSpec>> = {[K in keyof S]: ValueOf<S[K]>}

type Options<S extends Record<string, FilterParamSpec>> = {
  // Cascading clears: when the key changes, wipe its dependents. The hierarchy
  // filters need this — picking a new production area must not leave a line
  // from the previous one selected.
  resets?: Partial<Record<keyof S, ReadonlyArray<keyof S & string>>>
}

export type UseFilterParamsResult<S extends Record<string, FilterParamSpec>> = {
  values: Values<S>
  setValue: <K extends keyof S & string>(key: K, value: ValueOf<S[K]>) => void
  setValues: (patch: Partial<{[K in keyof S]: ValueOf<S[K]>}>) => void
  clearAll: () => void
  // How many dimensions are narrowing the result set — drives the "clear all"
  // affordance and the filter-count badge.
  activeCount: number
}

const LIST_SEPARATOR = ','

const readList = (raw: string | null): string[] =>
  raw ? raw.split(LIST_SEPARATOR).filter(Boolean) : []

/**
 * Binds a set of filters to the URL query string.
 *
 * Opt-in on purpose: `Filter` itself stays presentational so it can equally
 * serve modal filters and widget config, which hold draft state that must not
 * leak into the address bar. Pages that want deep links reach for this.
 *
 * Replaces three separate implementations of the same idea — the default
 * write-back in the since-deleted `helpers/FilterSelect`, the cascade clearing inside
 * `useHierarchyFilters`, and the hand-rolled `useSearchParams` juggling in the
 * signals, data-points, scheduled-prompts and subscriptions tables.
 */
export const useFilterParams = <const S extends Record<string, FilterParamSpec>>(
  spec: S,
  options: Options<S> = {},
): UseFilterParamsResult<S> => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Callers pass `spec` and `resets` as object literals, so their identity
  // changes every render. Depending on them directly would give `values` a new
  // identity on every render (breaking callers' effect deps) and re-run the
  // seeding effect forever. Key the memos on the serialised shape instead and
  // read the live objects through refs.
  const specKey = JSON.stringify(spec)
  const specRef = useRef(spec)
  specRef.current = spec
  const resetsRef = useRef(options.resets)
  resetsRef.current = options.resets

  const values = useMemo(() => {
    const result = {} as Values<S>
    for (const [key, entry] of Object.entries(specRef.current)) {
      const raw = searchParams.get(key)
      result[key as keyof S] = (
        entry.multiple ? readList(raw) : (raw ?? entry.default ?? '')
      ) as Values<S>[keyof S]
    }
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey, searchParams])

  // Seed defaults into the URL so a filtered view is shareable as-is. Only keys
  // that declare a default are touched, and only while absent — a user who
  // clears a filter back to its default keeps that in the URL rather than
  // fighting this effect.
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    let changed = false
    for (const [key, entry] of Object.entries(specRef.current)) {
      if (entry.default && !next.has(key)) {
        next.set(key, entry.default)
        changed = true
      }
    }
    if (changed) {
      setSearchParams(next, {replace: true})
    }
  }, [specKey, searchParams, setSearchParams])

  const applyPatch = useCallback(
    (patch: Record<string, string | string[]>, changedKey?: string) => {
      const next = new URLSearchParams(searchParams)

      for (const [key, value] of Object.entries(patch)) {
        const encoded = Array.isArray(value) ? value.join(LIST_SEPARATOR) : value
        if (encoded) {
          next.set(key, encoded)
        } else {
          next.delete(key)
        }
      }

      if (changedKey) {
        for (const dependent of resetsRef.current?.[changedKey] ?? []) {
          next.delete(dependent)
        }
      }

      // Bail out when nothing actually moved, so a re-render that re-applies
      // the same value does not restart effects keyed on the search params.
      if (next.toString() === searchParams.toString()) {
        return
      }

      setSearchParams(next, {replace: true})
    },
    [searchParams, setSearchParams],
  )

  const setValue = useCallback(
    <K extends keyof S & string>(key: K, value: ValueOf<S[K]>): void => {
      applyPatch({[key]: value as string | string[]}, key)
    },
    [applyPatch],
  )

  const setValues = useCallback(
    (patch: Partial<{[K in keyof S]: ValueOf<S[K]>}>): void => {
      applyPatch(patch as Record<string, string | string[]>)
    },
    [applyPatch],
  )

  const clearAll = useCallback((): void => {
    const next = new URLSearchParams(searchParams)
    for (const [key, entry] of Object.entries(specRef.current)) {
      if (entry.default) {
        next.set(key, entry.default)
      } else {
        next.delete(key)
      }
    }
    if (next.toString() === searchParams.toString()) {
      return
    }
    setSearchParams(next, {replace: true})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey, searchParams, setSearchParams])

  const activeCount = useMemo(() => {
    let count = 0
    for (const [key, entry] of Object.entries(specRef.current)) {
      const raw = searchParams.get(key)
      if (entry.multiple) {
        count += readList(raw).length
      } else if (raw && raw !== entry.default) {
        count += 1
      }
    }
    return count
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey, searchParams])

  return {values, setValue, setValues, clearAll, activeCount}
}

export default useFilterParams
